import * as React from 'react';
import { Accordion, AccordionDetails, Grid, TextField, Typography } from '@mui/material';
import { Formik, FormikErrors } from 'formik';
import PhoneInput, { CountryData } from 'react-phone-input-2';
import 'react-phone-input-2/lib/material.css';
import { getCountryFromTimezone } from 'src/utils/timezoneToCountry';
import { createInitialPrefix } from 'src/pages/Login/forms/SignupForm';
import FidoScript from 'src/components/FidoScript';
import { WhiteContainer, CustomErrorMessage } from 'src/style/common';
import {
  AccordionSummaryWrapper,
  EnrichmentTime,
  GenericFormProps,
  getEnrichmentTime,
  removeFidoObject,
  renderPreset,
} from 'src/pages/ManualLookup/GenericForm/utils';
import SubmitGridComponent from 'src/pages/ManualLookup/SubmitGrid';
import { ArrowForwardIosSharp } from '@mui/icons-material';

const GenericForm: React.FC<GenericFormProps> = ({
  updateScore,
  updateResolutionId,
  isLoading,
  setResolutionId,
  apiKey,
  jsKey,
  fieldName,
  changeTab,
  setDefaultValue,
  generalFormInitialValues,
  setGeneralFormInitialValues,
}) => {
  const [phoneInitialPrefix, setPhoneInitialPrefix] = React.useState<string>(getCountryFromTimezone() || createInitialPrefix()?.iso2 || 'us');
  const [firstNameIsNotEmpty, setFirstNameIsNotEmpty] = React.useState<boolean>(false);
  const [lastNameIsNotEmpty, setLastNameIsNotEmpty] = React.useState<boolean>(false);
  const [emailIsNotEmpty, setEmailIsNotEmpty] = React.useState<boolean>(false);
  const [phoneIsNotEmpty, setPhoneIsNotEmpty] = React.useState<boolean>(false);
  const [ipIsNotEmpty, setIpIsNotEmpty] = React.useState<boolean>(false);

  const [downloadScript, setDownloadScript] = React.useState<boolean>(false);
  const [browserIsNotEmpty, setBrowserIsNotEmpty] = React.useState<boolean>(false);
  const [enrichmentTime, setEnrichmentTime] = React.useState<EnrichmentTime>('extensive');
  const customerID = `TEST-${Math.random().toString(36).substr(2)}`;
  const [showErrors, setShowErrors] = React.useState<boolean>(false);

  React.useEffect(() => removeFidoObject(), []);
  return (
    <WhiteContainer style={{ boxShadow: 'none', marginTop: 0 }}>
      <Formik
        initialValues={{
          // initital state managed by the upper level component is needed in order to keep the form state when the user changes tabs or update the appkey
          email: generalFormInitialValues.email,
          customer_id: '',
          first_name: generalFormInitialValues.first_name,
          last_name: generalFormInitialValues.last_name,
          tax_id: '',
          ip: generalFormInitialValues.ip,
          phone: generalFormInitialValues.phone === '' ? phoneInitialPrefix : generalFormInitialValues.phone,
          phone_prefix: '',
          browser: false,
        }}
        validateOnChange={true}
        // eslint-disable-next-line complexity
        validate={(values) => {
          const errors: FormikErrors<{
            oneField: string;
            name: string;
          }> = {};
          setGeneralFormInitialValues({
            first_name: values.first_name,
            last_name: values.last_name,
            email: values.email,
            phone: values.phone,
            ip: values.ip,
            domain: { value: '', country: '' },
          });
          const noFirstName = (values.first_name && values.first_name.trim().length === 0) || !values.first_name;
          const noLastName = (values.last_name && values.last_name.trim().length === 0) || !values.last_name;
          const noEmail = (values.email && values.email.trim().length === 0) || !values.email;
          const noPhone = (values.phone && values.phone.trim().length < 4) || !values.phone || values.phone === phoneInitialPrefix.toLocaleLowerCase();
          const noIp = (values.ip && values.ip.trim().length === 0) || !values.ip;

          // Se c'è un campo oltre phone e email che è compilato, allora phone e email devono essere compilati
          const noRequiedFields = (!noFirstName || !noLastName || !noIp) && (noEmail || noPhone);

          if (noRequiedFields) {
            errors.oneField = 'At least Email and Phone must be filled';
          }

          if ((noFirstName && !noLastName) || (!noFirstName && noLastName)) {
            errors.name = 'First Name and Last Name must be filled or both must be empty';
          }
          setFirstNameIsNotEmpty(!noFirstName);
          setLastNameIsNotEmpty(!noLastName);
          setEmailIsNotEmpty(!noEmail);
          setPhoneIsNotEmpty(!noPhone);
          setIpIsNotEmpty(!noIp);
          setBrowserIsNotEmpty(values.browser);
          if (Object.keys(errors).length === 0) setShowErrors(false);
          return errors;
        }}
        // eslint-disable-next-line complexity
        onSubmit={(values) => {
          if (isLoading) {
            return;
          }
          if (phoneIsNotEmpty && !emailIsNotEmpty && !firstNameIsNotEmpty && !lastNameIsNotEmpty && !ipIsNotEmpty) {
            setDefaultValue(values.phone);
            changeTab(null, 1, true);
            return;
          }
          if (emailIsNotEmpty && !phoneIsNotEmpty && !firstNameIsNotEmpty && !lastNameIsNotEmpty && !ipIsNotEmpty) {
            setDefaultValue(values.email);
            changeTab(null, 2, true);
            return;
          }

          if (ipIsNotEmpty && !phoneIsNotEmpty && !firstNameIsNotEmpty && !lastNameIsNotEmpty && !emailIsNotEmpty) {
            setDefaultValue(values.ip);
            changeTab(null, 3, true);
            return;
          }
          if (browserIsNotEmpty && !phoneIsNotEmpty && !firstNameIsNotEmpty && !lastNameIsNotEmpty && !emailIsNotEmpty) {
            setDefaultValue(values.browser ? 'true' : 'false');
            changeTab(null, 4, true);
            return;
          }

          setDefaultValue(null);
          updateResolutionId({
            api_key: apiKey,
            customer_id: customerID,
          });
          updateScore({
            api_key: apiKey,
            first_name: values.first_name || null,
            last_name: values.last_name || null,
            email: values.email || null,
            ip: values.ip || null,
            browser: values.browser,
            user_agent: null,
            phone: values.phone?.length > 3 ? `+${values.phone}` : null,
            customer_id: customerID,
            max_enrichment_time: getEnrichmentTime(enrichmentTime),
          });
          setDownloadScript(false);

          removeFidoObject();
        }}
      >
        {(formikProps) => {
          return (
            <Grid container spacing={3} style={{ marginTop: -2 }}>
              <Grid item xs={12} md={6}>
                <TextField
                  variant="outlined"
                  label="First Name"
                  data-testid="first-name-input"
                  fullWidth={true}
                  helperText={formikProps.touched.first_name && formikProps.errors.first_name}
                  {...formikProps.getFieldProps('first_name')}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  variant="outlined"
                  fullWidth={true}
                  label="Last Name"
                  helperText={formikProps.touched.last_name && formikProps.errors.last_name}
                  {...formikProps.getFieldProps('last_name')}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  variant="outlined"
                  label="Email Address"
                  fullWidth={true}
                  helperText={formikProps.touched.email && formikProps.errors.email}
                  {...formikProps.getFieldProps('email')}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <PhoneInput
                  dropdownStyle={{ position: 'fixed' }}
                  country={phoneInitialPrefix.toLowerCase() || formikProps.getFieldProps('phone_prefix').value}
                  enableSearch
                  value={formikProps.getFieldProps('phone').value}
                  isValid={formikProps.values.phone.length > 0}
                  onChange={(phone, obj: CountryData) => {
                    formikProps.setFieldValue('phone_prefix', obj.dialCode, true);
                    setPhoneInitialPrefix('');
                    formikProps.setFieldValue('phone', phone, true);
                  }}
                  onBlur={() => {
                    formikProps.setFieldTouched('phone_prefix');
                    formikProps.validateField('phone_prefix');
                    formikProps.setFieldTouched('phone');
                    formikProps.validateField('phone');
                  }}
                  inputStyle={{ width: '100%', height: 53 }}
                  specialLabel="Phone Number"
                  enableLongNumbers
                />
              </Grid>
              <div style={{ width: '100%' }}>
                <Accordion
                  disableGutters
                  style={{
                    width: '100%',
                    boxShadow: '0 0 0 0',

                    marginTop: 24,
                    marginLeft: 10,
                  }}
                >
                  <AccordionSummaryWrapper expandIcon={<ArrowForwardIosSharp sx={{ fontSize: '0.9rem' }} />} aria-controls="panel1-content" id="panel1-header">
                    <Typography variant="body1" style={{ marginTop: '1px' }}>
                      Additional Data
                    </Typography>
                  </AccordionSummaryWrapper>
                  <AccordionDetails style={{ width: '100%' }}>
                    <Grid container spacing={3}>
                      <Grid item xs={12} md={6}>
                        <TextField
                          variant="outlined"
                          fullWidth={true}
                          label="IP Address"
                          helperText={formikProps.touched.ip && formikProps.errors.ip}
                          {...formikProps.getFieldProps('ip')}
                        />
                      </Grid>
                    </Grid>
                  </AccordionDetails>
                </Accordion>
              </div>
              {Object.values(formikProps.errors)[0] && showErrors && (
                <Grid item xs={12} style={{ padding: 0 }}>
                  <CustomErrorMessage>{Object.values(formikProps.errors)[0]}</CustomErrorMessage>
                </Grid>
              )}
              {renderPreset(fieldName, setResolutionId)}
              <SubmitGridComponent
                enrichmentTime={enrichmentTime}
                setEnrichmentTime={setEnrichmentTime}
                isDisabled={isLoading || (!phoneIsNotEmpty && !emailIsNotEmpty && !firstNameIsNotEmpty && !lastNameIsNotEmpty && !ipIsNotEmpty)}
                onClickFn={Object.keys(formikProps.errors).length > 0 ? () => setShowErrors(true) : () => formikProps.submitForm()}
              />
              {downloadScript && <FidoScript customer_id={customerID} apiKey={jsKey} />}
            </Grid>
          );
        }}
      </Formik>
    </WhiteContainer>
  );
};

export default GenericForm;
